import React, { useEffect, useMemo, useState } from "react";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import style from "./terms.module.css";

const INLINE_MARKDOWN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

function renderInlineMarkdown(text, keyPrefix) {
  return text.split(INLINE_MARKDOWN).filter(Boolean).map((part, index) => {
    const key = `${keyPrefix}-${index}`;

    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }

    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return (
        <a href={link[2]} key={key} rel="noreferrer" target="_blank">
          {link[1]}
        </a>
      );
    }

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

function parseTerms(markdown) {
  return markdown
    .split(/^##\s+/m)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      const [heading, ...body] = block.split(/\r?\n/);
      return { heading: heading.trim(), body: body.join("\n").trim() };
    });
}

const LIST_ITEM = /^\s*(?:\d+(?:\.\d+)*(?:[.)])?|[a-z][.)]|[•▪])\s+/i;

function reflowSourceLines(block) {
  const lines = block.split(/\r?\n/).filter((line) => line.trim());
  const visualLines = [];
  let currentLine = "";
  let previousLine = "";

  lines.forEach((sourceLine) => {
    const line = sourceLine.trim();
    const isListItem = LIST_ITEM.test(sourceLine);
    const startsNewSentence =
      /[.!?:]$/.test(previousLine) && /^[A-Z*(]/.test(line);

    if (currentLine && (isListItem || startsNewSentence)) {
      visualLines.push(currentLine);
      currentLine = line;
    } else {
      currentLine = currentLine ? `${currentLine} ${line}` : line;
    }

    previousLine = line;
  });

  if (currentLine) visualLines.push(currentLine);
  return visualLines.join("\n");
}

function renderTermsBody(body, sectionIndex) {
  return body
    .split(/\r?\n\s*\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, blockIndex) => (
      <p className={style.termsParagraph} key={`section-${sectionIndex}-block-${blockIndex}`}>
        {renderInlineMarkdown(
          reflowSourceLines(block),
          `section-${sectionIndex}-block-${blockIndex}`,
        )}
      </p>
    ));
}

export default function Terms() {
  const [termsMarkdown, setTermsMarkdown] = useState("");
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${process.env.PUBLIC_URL}/general_terms.md`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load terms and conditions.");
        return response.text();
      })
      .then(setTermsMarkdown)
      .catch((error) => {
        if (error.name !== "AbortError") setLoadError(true);
      });

    return () => controller.abort();
  }, []);

  const sections = useMemo(() => parseTerms(termsMarkdown), [termsMarkdown]);
  const documentTitle = sections[0]?.heading || "Terms and Conditions";
  const bodySections = sections.slice(1);

  return (
    <main className={style.termsPage}>
      <SiteHeader />

      <header className={style.termsHero}>
        <p>Legal</p>
        <h1>{documentTitle}</h1>
      </header>

      <article className={style.termsContent}>
        {!termsMarkdown && !loadError && (
          <p className={style.statusMessage}>Loading terms and conditions…</p>
        )}

        {loadError && (
          <p className={style.statusMessage} role="alert">
            The Terms and Conditions could not be loaded. Please refresh the page and try again.
          </p>
        )}

        {bodySections.map((section, index) => (
          <section className={style.termsSection} key={section.heading}>
            <h2>{section.heading}</h2>
            <div className={style.termsBody}>
              {renderTermsBody(section.body, index)}
            </div>
          </section>
        ))}
      </article>

      <SiteFooter />
    </main>
  );
}
